<script setup>
import { ref } from 'vue'
import RefPicker from './RefPicker.vue'
import AnyRecordPicker from './AnyRecordPicker.vue'
import { entityLabel } from '@/lib/labels.js'

/**
 * What an activity or note is about: {entity, uuid} of any record (D-43). No type chosen (as it
 * starts): found among all of them, as the search box finds; a type chosen: only among those.
 */
const props = defineProps({
  modelValue: { type: Object, default: null },
  reference: { type: Object, default: null },
  entities: { type: Array, default: () => ['account', 'contact', 'lead', 'opportunity', 'offer'] },
  disabled: Boolean,
})
const emit = defineEmits(['update:modelValue', 'select'])
const entity = ref('')
// The one picked here, with its name: shown when the type is changed back.
const picked = ref(null)
// A type chosen keeps what was picked when it is of that type.
function narrow() {
  if (entity.value && props.modelValue && props.modelValue.entity !== entity.value) emit('update:modelValue', null)
}
function pick(record) {
  picked.value = record ? { entity: entity.value, uuid: record.uuid, label: record.label } : null
  emit('select', picked.value)
  emit('update:modelValue', record ? { entity: entity.value, uuid: record.uuid } : null)
}
function pickAny(record) {
  picked.value = record ? { entity: record.entity, uuid: record.uuid, label: record.label } : null
  emit('select', picked.value)
}
</script>

<template>
  <div class="flex gap-2">
    <select v-model="entity" class="input w-36 shrink-0" :disabled="disabled" :aria-label="$t('common.recordType')" data-testid="regarding-type" @change="narrow">
      <option value="">{{ $t('common.allTypes') }}</option>
      <option v-for="e in entities" :key="e" :value="e">{{ entityLabel(e) }}</option>
    </select>
    <AnyRecordPicker
      v-if="!entity"
      class="min-w-0 flex-1"
      :entities="entities"
      :model-value="modelValue"
      :reference="picked || reference"
      :disabled="disabled"
      @select="pickAny"
      @update:model-value="(v) => emit('update:modelValue', v)"
    />
    <RefPicker
      v-else
      :key="entity"
      class="min-w-0 flex-1"
      :entity="entity"
      :model-value="modelValue?.entity === entity ? modelValue.uuid : null"
      :reference="[picked, reference].find((r) => r?.entity === entity) || null"
      :disabled="disabled"
      @select="pick"
    />
  </div>
</template>
